import React from "react";
import { Popover, Tooltip } from "antd";
import { localizeByKeyStatic } from "components/service";
import CommunicationConst from "../communication-const";
import CommunicationOriginalEmailField from "./communication-original-email-field";
import Icons from "components/icons";
import {
  getCommunicationConfidenceTone,
  normalizeCommunicationConfidence,
} from "../helpers/communication-confidence-helper";
import type { TCommunicationTask } from "../helpers/communication-types-helper";

interface ICommunicationInfoFieldProps {
  task: TCommunicationTask;
}

const workStateLabelMap: Record<TCommunicationTask["work_state"]["status"], string> = {
  untouched: "COMMUNICATION.WorkState_Untouched",
  in_progress: "COMMUNICATION.WorkState_InProgress",
  approved: "COMMUNICATION.WorkState_Approved",
};

const getCountLabel = (
  count: number,
  singularKey: string,
  pluralKey: string
): string =>
  localizeByKeyStatic(count === 1 ? singularKey : pluralKey, { count });

const formatStandNumberLabel = (value?: string | null): string => {
  const normalizedValue = typeof value === "string" ? value.trim() : "";

  if (!normalizedValue) {
    return "\u2014";
  }

  const withoutPrefix = normalizedValue.replace(/^(\u2116\s*|N\s*|#\s*)/iu, "").trim();

  return `\u2116 ${withoutPrefix || normalizedValue}`;
};

const getEmailSnippet = (task: TCommunicationTask): string => {
  const rawSnippet = task.ai_summary?.trim() || task.original_body?.trim() || "";

  return rawSnippet.replace(/\s+/g, " ").trim();
};

export const getSenderInitials = (senderStr: string): string => {
  const namePart = senderStr.replace(/<[^>]+>/g, "").trim();
  const words = namePart.split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  if (words.length === 1 && words[0].length >= 2) return words[0].slice(0, 2).toUpperCase();
  if (words.length === 1) return words[0][0].toUpperCase();
  return "?";
};

export const getAvatarHue = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash) % 360;
};

export const getSenderDisplayLabel = (
  task: Pick<TCommunicationTask, "sender" | "sender_email">
): string => {
  const senderEmail = typeof task.sender_email === "string" ? task.sender_email.trim() : "";

  return senderEmail || task.sender || "\u2014";
};

const stopRowNavigation = (event: React.MouseEvent<HTMLElement>) => {
  event.stopPropagation();
};

const CommunicationInfoField: React.FC<ICommunicationInfoFieldProps> = ({ task }) => {
  const [isPreviewOpen, setIsPreviewOpen] = React.useState(false);
  const confidence = normalizeCommunicationConfidence(task.confidence);
  const confidenceTone = getCommunicationConfidenceTone(confidence);
  const senderDisplayLabel = getSenderDisplayLabel(task);
  const avatarSeed = task.sender || senderDisplayLabel;
  const avatarInitials = getSenderInitials(avatarSeed);
  const avatarHue = getAvatarHue(avatarSeed);
  const requestCount = task.request_items.length;
  const requestGroupCount = task.request_groups.length;
  const openQuestions = task.questions.filter((question) => question.status === "open").length;
  const workStateLabel = localizeByKeyStatic(workStateLabelMap[task.work_state.status]);
  const snippet = getEmailSnippet(task);
  const shouldShowWorkState = task.work_state.status !== "untouched";
  const hasPreview = Boolean(task.original_body?.trim());
  const senderEmailHref = task.sender_email?.trim()
    ? `mailto:${task.sender_email.trim()}`
    : undefined;
  const standContextLabel =
    requestGroupCount > 1
      ? getCountLabel(
        requestGroupCount,
        "COMMUNICATION.Info_StandGroupsCount",
        "COMMUNICATION.Info_StandGroupsCountPlural"
      )
      : formatStandNumberLabel(task.stand_number);
  const contextSummary = [ task.company_name || "\u2014", standContextLabel ].filter(Boolean).join(" \u2022 ");
  const totalQuestions = task.questions.length;
  const isDismissed = task.status === CommunicationConst.statuses.DISMISSED;
  const hasSpamSignal = !isDismissed && task.intent === "spam";
  const hasProblems = !isDismissed && (openQuestions > 0 || task.status === CommunicationConst.statuses.UNIDENTIFIED);
  const problemLabel = task.status === CommunicationConst.statuses.UNIDENTIFIED
    ? localizeByKeyStatic("COMMUNICATION.Info_NeedsMatch")
    : getCountLabel(openQuestions, "COMMUNICATION.Info_OpenClarification", "COMMUNICATION.Info_OpenClarificationPlural");
  const dismissedReasonLabel = task.dismissed_reason
    ? localizeByKeyStatic(`COMMUNICATION.NotRelevantReason_${task.dismissed_reason}`)
    : "";

  return (
    <div className="communication-info-field">
      <div className="communication-info-field__header">
        <Tooltip title={task.subject}>
          <strong className="communication-info-field__subject">{task.subject || "\u2014"}</strong>
        </Tooltip>

        <span
          className={`communication-info-field__confidence communication-info-field__confidence--${confidenceTone}`}
          title={`${localizeByKeyStatic("TABLES.Column_Title_Confidence")}: ${confidence}%`}
        >
          {`${confidence}%`}
        </span>
      </div>

      <div className="communication-info-field__sender-row">
        <span
          className="communication-info-field__avatar"
          style={{ "--avatar-hue": `${avatarHue}` } as React.CSSProperties}
        >
          {avatarInitials}
        </span>
        {senderEmailHref ? (
          <Tooltip title={task.sender}>
            <a
              className="communication-info-field__sender"
              href={senderEmailHref}
              onMouseDown={stopRowNavigation}
            >
              {senderDisplayLabel}
            </a>
          </Tooltip>
        ) : (
          <Tooltip title={task.sender}>
            <span className="communication-info-field__sender">{senderDisplayLabel}</span>
          </Tooltip>
        )}
      </div>

      <div className="communication-info-field__context-row">
        <span className="communication-info-field__context" title={contextSummary}>
          {contextSummary}
        </span>
      </div>

      {snippet && (
        <span className="communication-info-field__snippet">{snippet}</span>
      )}

      {(task.demo_badge_key || shouldShowWorkState) && (
        <div className="communication-info-field__meta">
          {task.demo_badge_key && (
            <span className="communication-info-field__demo-badge">
              {localizeByKeyStatic(task.demo_badge_key)}
            </span>
          )}
          {shouldShowWorkState && (
            <span
              className={`communication-info-field__state communication-info-field__state--${task.work_state.status}`}
              title={localizeByKeyStatic("COMMUNICATION.WorkState_Label")}
            >
              {workStateLabel}
            </span>
          )}
        </div>
      )}

      <div className="communication-info-field__footer">
        <div className="communication-info-field__chips">
          {isDismissed && (
            <span className="communication-info-field__chip communication-info-field__chip--dismissed">
              {localizeByKeyStatic("COMMUNICATION.Info_NotRelevant")}
            </span>
          )}
          {isDismissed && dismissedReasonLabel && (
            <span className="communication-info-field__chip communication-info-field__chip--muted">
              {dismissedReasonLabel}
            </span>
          )}
          {hasSpamSignal && (
            <span className="communication-info-field__chip communication-info-field__chip--spam">
              {localizeByKeyStatic("COMMUNICATION.Info_SpamSignal")}
            </span>
          )}
          {requestCount > 0 && (
            <span className="communication-info-field__chip communication-info-field__chip--orders">
              {getCountLabel(requestCount, "COMMUNICATION.Info_ItemsCount", "COMMUNICATION.Info_ItemsCountPlural")}
            </span>
          )}
          {totalQuestions > 0 && (
            <span className="communication-info-field__chip communication-info-field__chip--questions">
              {getCountLabel(totalQuestions, "COMMUNICATION.Info_ChipQuestionsSingle", "COMMUNICATION.Info_ChipQuestions")}
            </span>
          )}
          {hasProblems && (
            <span className="communication-info-field__chip communication-info-field__chip--problem">
              {problemLabel}
            </span>
          )}
        </div>

        {hasPreview && (
          <div className="communication-info-field__preview-row">
            <Popover
              trigger="click"
              open={isPreviewOpen}
              onOpenChange={setIsPreviewOpen}
              placement="rightTop"
              overlayClassName="communication-info-field__popover-overlay"
              getPopupContainer={(trigger) =>
                trigger.closest(".page-communication") || document.body
              }
              content={(
                <div className="communication-info-field__email-tooltip">
                  <CommunicationOriginalEmailField
                    originalBody={task.original_body}
                    equipmentChanges={task.final_changes.equipment_changes}
                    headerMeta={task.original_email_meta}
                    isExpanded={true}
                  />
                </div>
              )}
            >
              <button
                type="button"
                className={`communication-info-field__preview-trigger${isPreviewOpen ? " communication-info-field__preview-trigger--active" : ""}`}
                onMouseDown={stopRowNavigation}
              >
                <Icons.Document />
                <span>{localizeByKeyStatic("COMMUNICATION.Section_AIContextSecondary")}</span>
              </button>
            </Popover>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunicationInfoField;
